import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { matchPath, useLocation } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';

import { Paths } from '../../../app/constants/paths';
import { customScrollPrimary } from '../../../app/global-styles';
import {
  useGetUserCollections,
  useGetUserSharedCollections,
  useGetUserSharedViewsV2,
  useGetUserViewsV2,
} from '../../../app/hooks/accounts';
import { TimeRangeUtil } from '../../../app/utils/time';
import { useTabManager } from '../../context/tab-manager-context';

import { CollectionTab } from './CollectionTab';
import { SaveCollectionModal } from './SaveCollectionModal';

import { Icon, Tooltip } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { useClickOutside } from '@controlrooms/hooks';
import { DateFormats, PersistCollection, PersistView, TimeSelection } from '@controlrooms/models';
import { TimeUtils } from '@controlrooms/utils';

const Tabs = styled.div`
  display: flex;
  position: relative;
  .container {
    display: flex;

    &.hasMoreTabs {
      .label {
        display: none;
      }
    }
  }

  .add-new,
  .viewsets-option {
    width: 50px;
    padding: 0px;
    height: 100%;
    svg {
      path {
        fill: ${({ theme }) => theme.v2.tab.textColor};
      }
    }
  }

  .tabs {
    height: 50px;
    background: ${({ theme }) => theme.v2.tab.tabContainerBG};
    flex-grow: 1;
    white-space: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
`;
// exported
export const TabBaseLine = styled.div`
  width: 100%;
  height: 2px;
  background: ${({ theme }) => theme.v2.tab.backgroundColor};

  width: 100%;
  height: 2px;
  box-shadow: none;
`;

// exported
export const Tab = styled.button<{ isActive?: boolean }>`
  border: none;
  border: ${({ theme }) => `1px solid ${theme.v2.tab.backgroundColor}`};
  border-right: ${({ theme }) => `1px solid ${theme.v2.tab.tabContainerBG}`};
  background-color: ${({ theme }) => theme.v2.tab.backgroundColor};
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 0px;
  flex-direction: column;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  .subMenu-link {
    padding: 10px;
  }
  ${({ isActive }) =>
    isActive &&
    `
    border-bottom: 2px solid #93BBC3;
    ${TabBaseLine} {
      background-color: #93BBC3;
      box-shadow: 0px -3px 10px 2px #93BBC3;
    }
  `}
  ${({ isActive, theme }) =>
    !isActive &&
    `
    .label {
      text-transform: capitalize;
      ::before {
        content: '';
        display: block;
        width: 100%;
        height: 20px;
        background-color: ${theme.v2.tab.backgroundColor};
      }
    }
  `}
    &:hover {
    border-bottom: 2px solid #93bbc3;
    ${TabBaseLine} {
      background-color: #93bbc3;
      box-shadow: 0px -3px 10px 2px #93bbc3;
    }
  }

  &:focus {
    outline: none;
  }

  &.viewsets-option {
    padding: 5px;
  }
  .tab-content {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &.collection {
  }
  .label {
    text-transform: capitalize;
    max-width: 150px;
    display: flex;
    flex: 1 0 0;
    padding-left: 10px;
    overflow: hidden;
    color: ${({ theme }) => theme.v2.tab.textColor};
    text-overflow: ellipsis;

    font-family: 'Open Sans';
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 13px;
    white-space: nowrap;
    align-items: center;

    ::before {
      content: '';
      display: block;
      width: 100%;
      height: 20px;
      background: ${({ theme }) => theme.v2.tab.backgroundColor};
    }
  }
`;
// exported
export const Menu = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0px;
  position: absolute;
  top: 40px;
  background-color: ${({ theme }) => theme.modal.contentBackgroundColor};
  border-radius: 4px;
  box-shadow: 0px 8px 12px 0px rgba(0, 0, 0, 0.6);

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    &.expanded-list {
      max-height: 300px;
      ${customScrollPrimary}
      overflow-y: auto;
    }

    li {
      display: flex;
      padding: 5px 25px;
      align-items: center;
      align-self: stretch;
      cursor: pointer;

      &.separator {
        border-bottom: ${({ theme }) => `1px solid ${theme.v2.tab.backgroundColor}`};
        background-color: ${({ theme }) => theme.modal.contentBackgroundColor};
      }

      &.disabled {
        cursor-event: not-allowed;
        opacity: 0.5;
      }

      &.option {
        font-family: 'Open Sans';
        font-size: 11px;
        font-style: normal;
        font-weight: 400;
        line-height: 13px;
        color: ${({ theme }) => theme.modal.panel.color};
        &:hover {
          background: ${({ theme }) => theme.sidebar.folders.hover.backgroundGradient};
        }
        .view-name {
          width: 300px;
          text-overflow: ellipsis;
          text-wrap: nowrap;
          overflow: hidden;
        }
        &:hover {
          .side-menu,
          .chevron {
            display: block;
          }
        }
        .chevron {
          display: none;
        }
        .side-menu {
          display: none;
          z-index: 2;
          position: fixed;
          color: ${({ theme }) => theme.modal.panel.color};
          border-radius: 3px;
          right: 10px;
          margin-top: 100px;
          background-color: ${({ theme }) => theme.modal.contentBackgroundColor};
          box-shadow: 0px 8px 12px 0px rgba(0, 0, 0, 0.6);
          li:hover {
            background: ${({ theme }) => theme.sidebar.folders.hover.backgroundGradient};
          }
        }
      }
      &.label {
        font-family: 'Open Sans';
        font-size: 9px;
        font-style: normal;
        font-weight: 700;
        line-height: 9px; /* 100% */
        text-transform: uppercase;
        background-color: ${({ theme }) => theme.sidebar.folders.headerBackgroundColor};
        color: ${({ theme }) => theme.modal.panel.color};
        margin-top: 0px;
      }
    }
  }
`;

export const FieldLabel = styled.label`
  color: #a0a38d;
  font-family: 'Open Sans';
  font-size: 11px;
  font-style: normal;
  font-weight: 300;
  line-height: 13px;
  margin-bottom: 5px;
  display: inline-block;
`;

export const preparePrimaryLabel = (timeselection: TimeSelection, customString = false) => {
  const hoursDisplay = TimeRangeUtil.calculateTimePreset(
    'label',
    timeselection.streamingTimeInSeconds || 0,
  );
  if (timeselection.nowSelected && !hoursDisplay) {
    return (
      <>
        {TimeUtils.toTimezone(timeselection.startTime, timeselection.timezone).format(
          DateFormats.DATE,
        )}{' '}
        - Now
      </>
    );
  }
  if (customString) {
    return (
      <span className="time">
        {TimeUtils.toTimezone(timeselection.startTime, timeselection.timezone).format(
          'MM-DD-YYYY HH:mm:ss z',
        )}{' '}
        -{' '}
        {TimeUtils.toTimezone(timeselection.endTime, timeselection.timezone).format(
          'MM-DD-YYYY HH:mm:ss z',
        )}
      </span>
    );
  }
  return (
    hoursDisplay || (
      <span className="time">
        {TimeUtils.toTimezone(timeselection.startTime, timeselection.timezone).format(
          'MM-DD-YYYY HH:mm:ss z',
        )}{' '}
        -{' '}
        {TimeUtils.toTimezone(timeselection.endTime, timeselection.timezone).format(
          'MM-DD-YYYY HH:mm:ss z',
        )}
      </span>
    )
  );
};

interface ViewsetsMenuButtonProps {
  trigger: React.ReactElement;
}

export const ViewsetsMenuButton: React.FC<ViewsetsMenuButtonProps> = ({ trigger }) => {
  const { data: userSharedViews } = useGetUserSharedViewsV2();
  const { data: useSharedCollections } = useGetUserSharedCollections();
  const { data: _userViews } = useGetUserViewsV2();
  const { data: userCollections } = useGetUserCollections();

  const userViews = useMemo(() => {
    return _userViews?.filter((v) => v.default_view === false) || [];
  }, [_userViews]);

  const sharedList = useMemo(() => {
    return [
      ...(userSharedViews || []).map((v) => {
        return {
          name: v.view.name,
          id: v.view_id || '',
          createdBy: v.created_by,
          userId: v.user_id,
          isCollection: false,
          entity: v,
        };
      }),
      ...(useSharedCollections || []).map((c) => {
        return {
          name: c.collection_name,
          id: c.collection_id || '',
          createdBy: '',
          userId: '',
          isCollection: true,
          entity: c,
        };
      }),
    ];
  }, [useSharedCollections, userSharedViews]);

  const { tabs, openSavedView, closeAllTabs, viewCollection, setViewCollection, loadCollection } =
    useTabManager();
  const viewIds = tabs.map((t) => t.id);
  const handleCloseAll = () => {
    closeAllTabs();
  };
  const [showCollectionSubmenu, setShowCollectionSubmenu] = useState(false);
  const [showSaveCollectionModal, setShowSaveCollectionModal] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(menuRef, () => setShowCollectionSubmenu(false));

  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const handleLoadView = async (view: PersistView) => {
    if (!view.view_id) return;
    await openSavedView(view.view_id);
    if (viewCollection.collectionId) {
      setViewCollection((prev) => ({
        ...prev,
        viewsIds: Array.from(
          new Set([...prev.viewsIds, view.view_id].filter((id): id is string => id !== undefined)),
        ),
        views: [...(prev.views || []).filter((v) => v.view_id !== view.view_id), view],
        isDirty: true,
      }));
    }
    setShowCollectionSubmenu(false);
  };

  const handleLoadCollection = async (collection: PersistCollection) => {
    if (collection.views) {
      await loadCollection(collection);
    }
    setShowCollectionSubmenu(false);
  };

  const handleCreateNewCollection = () => {
    setShowSaveCollectionModal(true);
    setShowCollectionSubmenu(false);
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setShowCollectionSubmenu((prev) => !prev);
  };

  const triggerWithHandler = React.cloneElement(trigger, {
    onClick: handleTriggerClick,
  });

  return (
    <>
      {triggerWithHandler}
      {showCollectionSubmenu &&
        menuPosition &&
        ReactDOM.createPortal(
          <Menu
            ref={menuRef}
            style={{
              position: 'absolute',
              top: menuPosition.top,
              right: 10,
              zIndex: 1000,
              color: '#faffda',
              width: '300px',
            }}
          >
            <ul>
              {viewIds.length ? (
                <li
                  className={`option`}
                  onClick={() => {
                    handleCloseAll();
                    setShowCollectionSubmenu(false);
                  }}
                >
                  Close All Views
                </li>
              ) : null}
              <li
                className={`option ${
                  viewIds.length === 1 && viewIds[0] === 'default' ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (viewIds.length) {
                    handleCreateNewCollection();
                  }
                }}
              >
                Save open tabs as new collection
              </li>
              {/* <li className={`option`}>Manage your views and collections</li> */}
              {userCollections?.length ? <li className="separator"></li> : null}
              {userCollections?.length ? <li className={`label`}>YOUR SAVED TAB SETS</li> : null}
              {userCollections?.length ? (
                <ul className="expanded-list">
                  <Virtuoso
                    style={{
                      height: `${(userCollections?.length || 1) * 23}px`,
                      maxHeight: '300px',
                    }}
                    className="my-view-container"
                    totalCount={userCollections ? userCollections.length : 0}
                    overscan={10}
                    itemContent={(index) => {
                      if (userCollections?.length) {
                        const collection = userCollections[index];
                        return (
                          <Tooltip label={collection.collection_name || 'Unnamed Collection'}>
                            <li
                              className={`option`}
                              onClick={() => {
                                handleLoadCollection(collection);
                              }}
                            >
                              {collection.collection_name || 'Unnamed Collection'}
                            </li>
                          </Tooltip>
                        );
                      }
                    }}
                  />
                </ul>
              ) : null}
              {userViews?.length ? <li className="separator"></li> : null}
              {userViews?.length ? <li className={`label`}>YOUR SAVED TABS</li> : null}
              {userViews?.length ? (
                <ul className="expanded-list">
                  <Virtuoso
                    style={{
                      height: `${(userViews?.length || 1) * 23}px`,
                      maxHeight: '300px',
                    }}
                    className="my-view-container"
                    totalCount={userViews ? userViews.length : 0}
                    overscan={10}
                    itemContent={(index) => {
                      if (userViews?.length) {
                        const view = userViews[index];
                        return (
                          <li
                            className={`option`}
                            onClick={() => {
                              handleLoadView(view);
                            }}
                          >
                            <div className="view-name">
                              <Tooltip label={view?.view?.name || 'Unnamed view'}>
                                {view?.view?.name || 'Unnamed view'}
                              </Tooltip>
                            </div>
                          </li>
                        );
                      }
                    }}
                  />
                </ul>
              ) : null}
              {sharedList?.length ? <li className="separator"></li> : null}
              {sharedList?.length ? <li className={`label`}>SHARED WITH YOU</li> : null}
              {sharedList?.length ? (
                <ul className="expanded-list">
                  <Virtuoso
                    style={{
                      height: `${(sharedList?.length || 1) * 23}px`,
                      maxHeight: '300px',
                    }}
                    className="my-view-container"
                    totalCount={sharedList ? sharedList.length : 0}
                    overscan={10}
                    itemContent={(index) => {
                      if (sharedList?.length) {
                        const viewCollection = sharedList[index];
                        return (
                          <li
                            className={`option`}
                            onClick={() => {
                              viewCollection.isCollection
                                ? handleLoadCollection(viewCollection.entity as PersistCollection)
                                : handleLoadView(viewCollection.entity as PersistView);
                            }}
                          >
                            {viewCollection.isCollection && (
                              <Icon name={ICONS.Folder} width="15" height="20" />
                            )}
                            <div
                              className="view-name"
                              style={{
                                marginLeft: viewCollection.isCollection ? '10px' : '0px',
                              }}
                            >
                              {viewCollection.name || 'Unnamed'}
                            </div>
                          </li>
                        );
                      }
                    }}
                  />
                </ul>
              ) : null}
            </ul>
          </Menu>,
          document.body,
        )}
      {showSaveCollectionModal && (
        <SaveCollectionModal onClose={() => setShowSaveCollectionModal(false)} />
      )}
    </>
  );
};

export const TabOptions = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const isView = matchPath(Paths.VIEWS, pathname);

  const { tabs, addNewTab, viewCollection } = useTabManager();
  const viewIds = tabs.map((t) => t.id);
  const hasNewTabs = viewIds.length > 3;

  const handleAddNewView = async () => {
    await addNewTab();
  };

  return (
    <Tabs>
      <div
        className={`container default ${hasNewTabs || viewCollection.name ? 'hasMoreTabs' : ''}`}
        id="default-tab-container"
      ></div>
      {viewCollection.name !== '' && isView && <CollectionTab />}
      <div className="container tabs" id="tab-container" ref={scrollRef}></div>
      {isView && (
        <div className="container menu" style={{ zIndex: 1 }}>
          <Tooltip label={'Add New View'}>
            <Tab className="add-new" onClick={() => handleAddNewView()}>
              <div className="tab-content">
                <Icon name={ICONS.Plus} width="15" height="20" />
              </div>
            </Tab>
          </Tooltip>
          <ViewsetsMenuButton
            trigger={
              <Tab className="viewsets-option">
                <div className="tab-content">
                  <Icon name={ICONS.Kebab} width="15" height="20" color="#A0A38D" />
                </div>
              </Tab>
            }
          />
        </div>
      )}
    </Tabs>
  );
};
